export interface SecurityHandler {
  validateInput: (input: string) => boolean;
  sanitizeOutput: (output: string) => string;
  checkPermissions: (action: string) => boolean;
  validate: (options: { files: Record<string, unknown>[] }) => Promise<{ 
    success: boolean; 
    errors?: string[];
    summary: { totalFiles: number; validFiles: number; invalidFiles: number };
    validationResults: Array<{ valid: boolean; reason: string | null }>;
  }>;
  scan: (options: { file?: Record<string, unknown>; content?: string; filePath?: string; fileName?: string; fileSize?: number; mimeType?: string; sessionId?: string }) => Promise<{ 
    threats: Record<string, unknown>[]; 
    safe: boolean;
    status: string;
    scanId: string;
    scanTime: string;
  }>;
}

export const arcSecurityHandler: SecurityHandler = {
  validateInput: (input: string): boolean => {
    if (!input || typeof input !== 'string') return false;
    if (input.length > 10000) return false;
    const dangerousPatterns = [/<script/i, /javascript:/i, /on\w+=/i];
    return !dangerousPatterns.some(pattern => pattern.test(input));
  },

  sanitizeOutput: (output: string): string => {
    if (!output || typeof output !== 'string') return '';
    return output
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;');
  },

  checkPermissions: (action: string): boolean => {
    const allowedActions = ['read', 'write', 'execute', 'admin'];
    return allowedActions.includes(action);
  },

  validate: async (options: { files: Record<string, unknown>[] }): Promise<{ 
    success: boolean; 
    errors?: string[];
    summary: { totalFiles: number; validFiles: number; invalidFiles: number };
    validationResults: Array<{ valid: boolean; reason: string | null }>;
  }> => {
    const errors: string[] = [];
    const validationResults: Array<{ valid: boolean; reason: string | null }> = [];
    let validFiles = 0;
    let invalidFiles = 0;
    
    for (const file of options.files) {
      let fileValid = true;
      let reason: string | null = null;
      
      if (!file.name || typeof file.name !== 'string') {
        errors.push('Invalid file name');
        fileValid = false;
        reason = 'Invalid file name';
        invalidFiles++;
      } else if (file.size && typeof file.size === 'number' && file.size > 50 * 1024 * 1024) {
        errors.push(`File ${file.name} exceeds size limit`);
        fileValid = false;
        reason = 'File too large';
        invalidFiles++;
      } else {
        const dangerousExtensions = ['.exe', '.bat', '.cmd', '.scr', '.vbs'];
        const fileName = file.name as string;
        const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
        if (dangerousExtensions.includes(extension)) {
          errors.push(`File ${fileName} has dangerous extension`);
          fileValid = false;
          reason = 'Dangerous file extension';
          invalidFiles++;
        } else {
          validFiles++;
        }
      }
      
      validationResults.push({ valid: fileValid, reason });
    }
    
    return {
      success: errors.length === 0,
      errors: errors.length > 0 ? errors : undefined,
      summary: {
        totalFiles: options.files.length,
        validFiles,
        invalidFiles
      },
      validationResults
    };
  },

  scan: async (options: { file?: Record<string, unknown>; content?: string; filePath?: string; fileName?: string; fileSize?: number; mimeType?: string; sessionId?: string }): Promise<{ 
    threats: Record<string, unknown>[]; 
    safe: boolean;
    status: string;
    scanId: string;
    scanTime: string;
  }> => {
    const threats: Record<string, unknown>[] = [];
    
    if (options.content) {
      const dangerousPatterns = [
        /<script[^>]*>/i,
        /javascript:/i,
        /on\w+\s*=/i,
        /eval\s*\(/i,
        /document\.write/i
      ];
      
      for (const pattern of dangerousPatterns) {
        if (pattern.test(options.content)) {
          threats.push({
            type: 'malicious_code',
            pattern: pattern.source,
            severity: 'high'
          });
        }
      }
    }
    
    const fileName = options.fileName || (options.file?.name as string);
    if (fileName && typeof fileName === 'string') {
      const suspiciousNames = [
        /virus/i,
        /malware/i,
        /trojan/i,
        /backdoor/i,
        /eicar/i
      ];
      
      for (const pattern of suspiciousNames) {
        if (pattern.test(fileName)) {
          threats.push({
            type: 'suspicious_filename',
            pattern: pattern.source,
            severity: 'medium'
          });
          if (/eicar/i.test(fileName)) {
            threats.push({
              type: 'test_file',
              name: 'EICAR-Test-File',
              severity: 'high'
            });
          }
        }
      }
    }
    
    const safe = threats.length === 0;
    let status = 'clean';
    if (threats.length > 0) {
      status = threats.some(t => t.severity === 'high') ? 'infected' : 'infected';
    }
    
    return {
      threats,
      safe,
      status,
      scanId: `scan_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      scanTime: new Date().toISOString()
    };
  }
};
